import { useCallback, useState } from 'react';
import { useParams } from 'react-router';

const PIN_KEYS = ['entityType', 'status'];

export const PIN_STATES = {
	DISABLED: 'disabled',
	PIN: 'pin',
	PINNED: 'pinned',
	UPDATE: 'update',
	CLEAR: 'clear',
};

export const pinStorageKey = (tenantId) =>
	`entityDashboard:pinnedFilters:${tenantId}`;

const hasActiveFilters = (filters) => PIN_KEYS.some((key) => filters[key]);

// Only known keys with string values are restored so a stale entry can't send
// an unexpected filter to the API.
const readStoredPin = (storageKey) => {
	try {
		const stored = JSON.parse(localStorage.getItem(storageKey));

		if (!stored || typeof stored !== 'object') return null;

		const pin = PIN_KEYS.reduce(
			(filters, key) => ({
				...filters,
				[key]: typeof stored[key] === 'string' ? stored[key] : '',
			}),
			{}
		);

		return hasActiveFilters(pin) ? pin : null;
	} catch {
		return null;
	}
};

const writeStoredPin = (storageKey, pin) => {
	try {
		if (pin) {
			localStorage.setItem(storageKey, JSON.stringify(pin));
		} else {
			localStorage.removeItem(storageKey);
		}
	} catch {
		// storage unavailable (private mode, quota); the pin still holds for the session
	}
};

export const getPinState = (pin, filters) => {
	const active = hasActiveFilters(filters);

	if (!pin) return active ? PIN_STATES.PIN : PIN_STATES.DISABLED;
	if (!active) return PIN_STATES.CLEAR;

	return PIN_KEYS.every((key) => pin[key] === filters[key])
		? PIN_STATES.PINNED
		: PIN_STATES.UPDATE;
};

// The pin is read synchronously on first render so the page can seed its
// filters from it before the first fetch.
const useEntityDashboardPin = () => {
	const { tenantId } = useParams();
	const storageKey = pinStorageKey(tenantId);

	const [pin, setPinState] = useState(() => readStoredPin(storageKey));

	const setPin = useCallback(
		(filters) => {
			const next =
				filters && hasActiveFilters(filters)
					? { entityType: filters.entityType, status: filters.status }
					: null;

			setPinState(next);
			writeStoredPin(storageKey, next);
		},
		[storageKey]
	);

	return { pin, setPin };
};

export default useEntityDashboardPin;
